import React, { useState, useEffect, useRef } from 'react';
import {
  Box, IconButton, Tooltip, Badge, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Typography, List, ListItem, ListItemIcon,
  ListItemText, ListItemSecondaryAction, CircularProgress, Chip
} from '@mui/material';
import {
  AttachFile as AttachIcon, CloudUpload as UploadIcon,
  Download as DownloadIcon, Delete as DeleteIcon, Close as CloseIcon,
  PictureAsPdf as PdfIcon, Image as ImageIcon, InsertDriveFile as FileIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/axiosInstance';
import config from '../../config';
import AttachmentPreviewDialog from './AttachmentPreviewDialog';

interface Attachment {
  attachmentId: number;
  fileName: string;
  originalFileName: string;
  fileExtension: string;
  fileSizeDisplay: string;
  uploadedByName: string;
  uploadedDate: string;
}

interface AttachmentSectionProps {
  documentType: string;
  documentId: number;
  isRTL?: boolean;
  disabled?: boolean;
  // النمط المتحكَّم به: عند تمرير open/onClose تُدار النافذة من الأب ويُخفى الزر الافتراضي
  open?: boolean;
  onClose?: () => void;
  hideTrigger?: boolean;
  // بيانات النطاق تُرفق عند الرفع للحفاظ على عزل الفروع/المخازن/العهد
  branchId?: number | null;
  warehouseId?: number | null;
  custodyId?: number | null;
  // عنوان اختياري يُعرض في النافذة (مثل رقم السند)
  titleSuffix?: string;
  // يُستدعى بعد أي رفع/حذف لتحديث العدّادات في الأب
  onChanged?: () => void;
}

const getFileIcon = (ext: string) => {
  const e = (ext || '').toLowerCase().replace('.', '');
  if (e === 'pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return <ImageIcon sx={{ color: '#43a047' }} />;
  return <FileIcon sx={{ color: '#757575' }} />;
};

const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  documentType, documentId, isRTL = false, disabled = false,
  open: controlledOpen, onClose, hideTrigger = false,
  branchId = null, warehouseId = null, custodyId = null, titleSuffix, onChanged,
}) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen! : internalOpen;
  const setOpen = (v: boolean) => {
    if (isControlled) { if (!v) onClose?.(); }
    else setInternalOpen(v);
  };
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewAtt, setPreviewAtt] = useState<Attachment | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadCount = async () => {
    try {
      const res = await axiosInstance.get(`/DocumentAttachments/count/${documentType}/${documentId}`);
      setCount(res.data?.count ?? res.data ?? 0);
    } catch { setCount(0); }
  };

  const loadAttachments = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/DocumentAttachments/by-document/${documentType}/${documentId}`);
      setAttachments(res.data || []);
      setCount((res.data || []).length);
    } catch { setAttachments([]); }
    setLoading(false);
  };

  useEffect(() => { if (documentId > 0) loadCount(); }, [documentId]);
  useEffect(() => { if (open && documentId > 0) loadAttachments(); }, [open]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      formData.append('documentId', documentId.toString());
      if (branchId) formData.append('branchId', String(branchId));
      if (warehouseId) formData.append('warehouseId', String(warehouseId));
      if (custodyId) formData.append('custodyId', String(custodyId));
      await axiosInstance.post('/DocumentAttachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(isRTL ? 'تم رفع الملف' : 'File uploaded');
      await loadAttachments();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || (isRTL ? 'خطأ في رفع الملف' : 'Upload failed'));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDownload = (id: number, fileName: string) => {
    const token = localStorage.getItem('token');
    const url = `${config.API_BASE_URL}/DocumentAttachments/download/${id}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    // Use fetch with auth header for download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  };

  const handleDelete = async (id: number) => {
    try {
      await axiosInstance.delete(`/DocumentAttachments/${id}`);
      toast.success(isRTL ? 'تم حذف الملف' : 'File deleted');
      await loadAttachments();
      onChanged?.();
    } catch { toast.error(isRTL ? 'خطأ في الحذف' : 'Delete failed'); }
  };

  return (
    <>
      {!hideTrigger && (
        <Tooltip title={isRTL ? 'المرفقات' : 'Attachments'}>
          <IconButton size="small" onClick={() => setOpen(true)} disabled={disabled}>
            <Badge badgeContent={count} color="primary" max={9} invisible={count === 0}>
              <AttachIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            {isRTL ? 'المرفقات' : 'Attachments'}{titleSuffix ? ` — ${titleSuffix}` : ''} ({count})
          </Typography>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : attachments.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={2}>
              {isRTL ? 'لا توجد مرفقات' : 'No attachments'}
            </Typography>
          ) : (
            <List dense>
              {attachments.map(att => (
                <ListItem key={att.attachmentId}>
                  <ListItemIcon>{getFileIcon(att.fileExtension)}</ListItemIcon>
                  <ListItemText
                    primary={att.originalFileName}
                    secondary={`${att.fileSizeDisplay} - ${att.uploadedByName} - ${new Date(att.uploadedDate).toLocaleDateString('en-GB')}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={isRTL ? 'عرض' : 'Preview'}>
                      <IconButton size="small" color="primary" onClick={() => setPreviewAtt(att)}>
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isRTL ? 'تنزيل' : 'Download'}>
                      <IconButton size="small" onClick={() => handleDownload(att.attachmentId, att.originalFileName)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(att.attachmentId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <input type="file" ref={fileRef} hidden onChange={handleUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
          <Button startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />} variant="contained" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {isRTL ? 'رفع ملف' : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>

      {previewAtt && (
        <AttachmentPreviewDialog
          open={!!previewAtt}
          onClose={() => setPreviewAtt(null)}
          attachmentId={previewAtt.attachmentId}
          fileName={previewAtt.originalFileName}
          fileExtension={previewAtt.fileExtension}
          isRTL={isRTL}
        />
      )}
    </>
  );
};

export default AttachmentSection;
